/**
 * **打断预示动画的纯逻辑。**
 *
 * 用户要的（`docs/interrupt-rework-plan.md` §6 / §11.1）：
 *
 * > 在某人拖动 [断点] 期间，所有人看到一张 [断点] 从一个**斜上方**的角度
 * > 淡入、飞向屏幕中心；进度直接绑定那张牌的**行程**，超过 100% 的部分用对数；
 * > 多人同时时角度互相避让，但不改终点。
 *
 * 这里只放**不碰 Pixi** 的那几件事，所以能在 node 里钉住：
 *
 * | 函数 | 干什么 |
 * | --- | --- |
 * | `entryAngles` | 给同时活跃的若干打断者分配互不重叠的入场角 |
 * | `interruptProgress` | 拖拽行程 → 动画进度（>1 走对数） |
 * | `interruptOpacity` | 行程 → 不透明度（按下 0、拖到三成满） |
 * | `endpointJitter` | 按 id 确定性生成终点的位置 / 旋转偏移 |
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * 入场角的弧（弧度，**屏幕坐标 y 向下**）。
 *
 * 210°..330° 正好是"斜上方"：左上是 225°、正上是 270°、右上是 315°。
 * 所有观众用同一套，所以同一场里大家看到的进场方向一致。
 */
export const ENTRY_ARC_START = (210 * Math.PI) / 180;
export const ENTRY_ARC_END = (330 * Math.PI) / 180;

/**
 * **给同时活跃的打断者分配入场角。**
 *
 * - 按 id 排序后再均分，所以只要活跃集合一样，所有观众算出来的一模一样；
 * - 取每一段的**中点**，人少时也落在弧里侧，不会顶着弧的两端；
 * - 人数变化时角度会重排 —— 这正是"互相避让"：谁都不和别人重叠。
 *
 * ⚠️ 终点不受这里影响（终点由 `endpointJitter` + 屏幕中心决定）。
 */
export function entryAngles(ids: readonly string[]): ReadonlyMap<string, number> {
  const sorted = [...ids].sort();
  const n = sorted.length;
  const map = new Map<string, number>();
  if (n === 0) return map;
  const span = ENTRY_ARC_END - ENTRY_ARC_START;
  for (let i = 0; i < n; i++) {
    const id = sorted[i];
    if (id === undefined) continue;
    map.set(id, ENTRY_ARC_START + (span * (i + 0.5)) / n);
  }
  return map;
}

/**
 * **拖拽行程 → 动画进度。**
 *
 * `raw` 是"牌已经走了整段行程的多少"：0 = 还在起点、1 = 到了屏幕中心、
 * >1 = 拖过头了。前 100% 线性；超过之后走对数，于是继续拖只会**缓慢**推进，
 * 不会让牌飞出去。
 */
export function interruptProgress(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  if (raw <= 1) return raw;
  return 1 + Math.log(raw);
}

/** 进度 → 0..1 的透明度进度：`fadeAt` 之前线性淡入，之后恒为 1 */
export function interruptOpacity(raw: number, fadeAt = 0.3): number {
  const p = interruptProgress(raw);
  if (p <= 0) return 0;
  if (p >= fadeAt) return 1;
  return p / fadeAt;
}

/** 终点允许偏离屏幕中心的最大半径（相对画布短边） */
export const ENDPOINT_RADIUS_SHARE = 0.12;

/**
 * **终点的随机位置 / 旋转偏移。**
 *
 * 按 id 确定性生成：同一个打断者在所有观众屏幕上停在同一处（"随机"指相对
 * 屏幕中心没有规律，而不是每次重画都跳）。旋转范围 ±12°。
 */
export function endpointJitter(
  id: string,
  radius: number,
): { readonly x: number; readonly y: number; readonly rotation: number } {
  const h = fnv1a(id);
  const a = ((h % 1000) / 1000) * Math.PI * 2;
  const r = (((h >>> 10) % 1000) / 1000) * radius;
  const rot = ((((h >>> 20) % 1000) / 1000) - 0.5) * ((24 * Math.PI) / 180);
  return { x: Math.cos(a) * r, y: Math.sin(a) * r, rotation: rot };
}

/**
 * **拖拽行程：牌向屏幕中心走了整段路程的多少。**
 *
 * 用**投影**而不是直线距离：横着乱拖不会让进度涨；只有真的朝中心去才算。
 * `start` 是按下时那张牌的位置（让观众看到的起点），`current` 是现在的位置，
 * `target` 是屏幕中心。返回 0..∞：1 = 正好到中心，>1 = 拖过头。
 */
export function travelProgress(input: {
  readonly start: Point;
  readonly current: Point;
  readonly target: Point;
}): number {
  const dx = input.target.x - input.start.x;
  const dy = input.target.y - input.start.y;
  const total = Math.hypot(dx, dy);
  if (total <= 0) return 0;
  const moved = (input.current.x - input.start.x) * dx + (input.current.y - input.start.y) * dy;
  const toward = moved / total;
  return toward <= 0 ? 0 : toward / total;
}

/** FNV-1a 32 位。和 `core/hash.ts` 同一个算法，够稳定也够快 */
function fnv1a(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
